import type {ReactNode} from 'react'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, MARKS} from '@contentful/rich-text-types'
import type {Document} from '@contentful/rich-text-types'
import inlineCodeStyles from './inlineCode.module.css'

/**
 * Temporary variant type for component-specific inline code styling.
 * Headings and descriptions need different line-heights to prevent
 * overlapping <code> padding/borders on multi-line text.
 * Remove once Primer Brand ships component-level inline code styles.
 */
export type InlineCodeVariant = 'heading' | 'headingRiver' | 'headingPillar' | 'description'

const variantClassMap: Record<InlineCodeVariant, string> = {
  heading: inlineCodeStyles.inlineCodeHeading,
  headingRiver: inlineCodeStyles.inlineCodeHeadingRiver,
  headingPillar: inlineCodeStyles.inlineCodeHeadingPillar,
  description: inlineCodeStyles.inlineCodeDescription,
}

export function createCodeMarkRenderer(className = inlineCodeStyles.inlineCode) {
  return {
    [MARKS.CODE]: (children: ReactNode) => <code className={className}>{children}</code>,
  }
}

export const codeMarkRenderer = createCodeMarkRenderer()
export const codeMarkRendererHeading = createCodeMarkRenderer(inlineCodeStyles.inlineCodeHeading)
export const codeMarkRendererHeadingRiver = createCodeMarkRenderer(inlineCodeStyles.inlineCodeHeadingRiver)
export const codeMarkRendererHeadingPillar = createCodeMarkRenderer(inlineCodeStyles.inlineCodeHeadingPillar)
export const codeMarkRendererDescription = createCodeMarkRenderer(inlineCodeStyles.inlineCodeDescription)

export const noCodeMarkRenderer = {
  [MARKS.CODE]: (children: ReactNode) => children,
}

/**
 * Renders the first paragraph node of a Contentful RichText document with optional inline code mark support.
 * All other nodes are silently ignored to enforce single-line content.
 * Used for headingRich fields (Card, CTABanner, River, Pillar) and description fields (Hero, CTABanner).
 *
 * @param document - The Contentful rich text document to render
 * @param enableCodeBlocks - Whether to enable code block rendering (controlled by contentful_primer_code_blocks feature flag)
 * @param codeVariant - Optional variant for component-specific inline code styling ('heading' | 'description')
 */
export function renderSingleLineRichText(
  document: Document,
  enableCodeBlocks = false,
  codeVariant?: InlineCodeVariant,
): ReactNode {
  const firstParagraph = document.content.find(node => node.nodeType === BLOCKS.PARAGRAPH)
  const singleParagraphDoc: Document = {
    ...document,
    content: firstParagraph ? [firstParagraph] : [],
  }
  const markRenderer = codeVariant ? createCodeMarkRenderer(variantClassMap[codeVariant]) : codeMarkRenderer
  return documentToReactComponents(singleParagraphDoc, {
    renderMark: enableCodeBlocks ? markRenderer : noCodeMarkRenderer,
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
    },
  })
}
